/* eslint-disable react/prop-types */
/* eslint-disable no-alert */
import './ExternalAuthorization.scss';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faArrowRight,
  faArrowLeft,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import BankService from '../../Services/BankService';
import ClientService from '../../Services/ClientService';
import CustomerService from '../../Services/CustomerService';
import SKVService from '../../Services/SKVService';
import AuthService from '../../Services/AuthService';
import XpektorLogo from '../../Images/xpektor_logo_new.svg';
import XpektorModal from '../../Components/XpektorModal/XpektorModal';
import XpektorButton from '../../Components/XpektorButton/XpektorButton';
import ArrowButton from '../../Components/ArrowButton/ArrowButton';
import LoadingBar from '../../Components/LoadingBar/LoadingBar';
import AccountingAuthStep from './AuthStates/AccountingAuthStep';
import BankAuthStep from './AuthStates/BankAuthStep';
import SkvAuthStep from './AuthStates/SkvAuthStep';
import BankTypeModal from './Modals/BankTypeModal';
import {
  AuthStepDisplay,
  AuthStepIcon,
  AuthStepIconLine,
} from './ExternalAuthComponents';

const gT = (t, key) => t(`page-external-authorization${key}`);

const AUTHSTATE = Object.freeze({
  ERROR: 'ERROR',
  SKV: 'SKV',
  BANK: 'BANK',
  ACCOUNTING: 'ACCOUNTING',
  COMPLETED: 'COMPLETED',
  LOADING: 'LOADING',
});

const URL_PARAM_ACTION = Object.freeze({
  AUTH: 'auth',
  CREATE_CLIENT: 'createClient',
  COMPLETED: 'completed',
  UNAUTHORIZED: 'unauthorized',
});

export default function ExternalAuthorization() {
  const { t } = useTranslation('app');
  const [authState, setAuthState] = useState('');

  const [errorText, setErrorText] = useState('');
  const [authErrorType, setAuthErrorType] = useState('');
  const [authErrorText, setAuthErrorText] = useState(null);

  const [externalAuthState, setExternalAuthState] = useState(null);
  const [showBankTypeModal, setShowBankTypeModal] = useState(false);

  const { search } = useLocation();
  const [logo, setLogo] = useState('');
  const [skvUrl, setSkvUrl] = useState('');
  const [fetchingSkvUrl, setFetchingSkvUrl] = useState(false);

  const state = new URLSearchParams(search).get('state');

  // Bank stuff
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState({});

  const countries = [
    { name: gT(t, '.countries.dk.name'), code: gT(t, '.countries.dk.code') },
    { name: gT(t, '.countries.fi.name'), code: gT(t, '.countries.fi.code') },
    { name: gT(t, '.countries.no.name'), code: gT(t, '.countries.no.code') },
    { name: gT(t, '.countries.se.name'), code: gT(t, '.countries.se.code') },
  ];
  const [selectedCountry, setSelectedCountry] = useState({
    name: gT(t, '.countries.se.name'),
    code: gT(t, '.countries.se.code'),
  });

  const getBanks = async (countryCode) => {
    await BankService.getBanks(countryCode)
      .then((response) => {
        setBanks(response.data);
        return true;
      })
      .catch(() => {
        setErrorText(gT(t, '.error.something-went-wrong'));
        setAuthState(AUTHSTATE.ERROR);
      });
    return false;
  };

  useEffect(() => {
    const getStepRelatedData = async () => {
      if (
        authState === AUTHSTATE.SKV &&
        externalAuthState.skvConnected === false
      ) {
        if (skvUrl.length === 0) {
          setFetchingSkvUrl(true);
          await SKVService.getDeepLinkSKV(externalAuthState.targetClient)
            .then((response) => {
              setSkvUrl(response.data);
            })
            .catch(() => {})
            .finally(() => {
              setFetchingSkvUrl(false);
            });
        }
      }
    };

    getStepRelatedData();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [authState]);

  const onExternalAuthStateFetch = async (extAuthState) => {
    await CustomerService.getOnboardingInfo()
      .then((response) => {
        setLogo(response.data.logo);
      })
      .catch(() => {});

    if (extAuthState.bankConnected === false) {
      await getBanks(selectedCountry.code)
        .then(() => setAuthState(AUTHSTATE.BANK))
        .catch(() => {});
    } else if (extAuthState.accountingConnected === false) {
      setAuthState(AUTHSTATE.ACCOUNTING);
    } else if (extAuthState.skvConnected === false) {
      setAuthState(AUTHSTATE.SKV);
    } else {
      setAuthState(AUTHSTATE.COMPLETED);
    }
  };

  useEffect(() => {
    const decodeState = async () => {
      if (state) {
        setAuthState(AUTHSTATE.LOADING);
        const response = await AuthService.decodeExternal(state);
        if (response != null) {
          setExternalAuthState(response);
          await onExternalAuthStateFetch(response);
          return;
        }
        window.location = `/authorization?action=unauthorized`;
      }
    };
    decodeState();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state]);

  useEffect(() => {
    const onAction = async () => {
      const usp = new URLSearchParams(search);
      const action = usp.get('action');
      const ext = usp.get('ext');
      const errorType = usp.get('error_type');
      const errorDescription = usp.get('error_description');
      const authCode = usp.get('authCode');
      const partnerId = usp.get('partnerId');
      const pId = partnerId !== null ? Number(partnerId) : null;

      if (!action) return;

      setAuthState(AUTHSTATE.LOADING);

      if (action === URL_PARAM_ACTION.AUTH) {
        await AuthService.getExternalAuthState(ext)
          .then((response) => {
            if (errorType !== null) {
              if (errorType === 'accounting') {
                setAuthState(AUTHSTATE.ACCOUNTING);
                setAuthErrorType(AUTHSTATE.ACCOUNTING);
                if (errorDescription !== null) {
                  /* eslint-disable-next-line prettier/prettier */
                  setAuthErrorText(
                    `${gT(t, '.error.connecting-accounting')} ${gT(
                      t,
                      '.error-message'
                    )}: ${errorDescription}`
                  );
                }
              } else if (errorType === 'bank') {
                getBanks(selectedCountry.code)
                  .then(() => setAuthState(AUTHSTATE.BANK))
                  .catch(() => {});
                setAuthErrorType(AUTHSTATE.BANK);
                if (errorDescription !== null && errorDescription !== 'null') {
                  /* eslint-disable-next-line prettier/prettier */
                  setAuthErrorText(
                    `${gT(t, '.error.connecting-bank')} ${gT(
                      t,
                      '.error-message'
                    )}: ${errorDescription}`
                  );
                }
              }
            } else {
              onExternalAuthStateFetch(response.data);
            }
            setExternalAuthState(response.data);
          })
          .catch(() => {});
      } else if (action === URL_PARAM_ACTION.CREATE_CLIENT) {
        // Land here after being redirected back from accounting auth
        await ClientService.authenticateAccounting(authCode, pId, ext)
          .then(() => {
            window.location = `/authorization?action=auth&ext=${ext}`;
          })
          .catch(() => {
            AuthService.getExternalAuthState(ext).then((response) => {
              setExternalAuthState(response.data);
              setAuthState(AUTHSTATE.ACCOUNTING);
              setAuthErrorType(AUTHSTATE.ACCOUNTING);
              if (errorDescription !== null) {
                /* eslint-disable-next-line prettier/prettier */
                setAuthErrorText(
                  `${gT(t, '.error.connecting-accounting')} ${gT(
                    t,
                    '.error-message'
                  )}: ${errorDescription}`
                );
              }
            });
          });
      } else if (action === URL_PARAM_ACTION.COMPLETED) {
        setAuthState(AUTHSTATE.COMPLETED);
      } else if (action === URL_PARAM_ACTION.UNAUTHORIZED) {
        setAuthState(AUTHSTATE.ERROR);
        setErrorText(gT(t, '.error.invalid-link'));
      }
    };
    onAction();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [search]);

  const redirectToFortnox = async () => {
    await AuthService.getAuthUrlFortnox(externalAuthState.id).then(
      (response) => {
        if (response) {
          window.location.replace(response);
        }
      }
    );
  };

  const redirectToVisma = async () => {
    await AuthService.getAuthUrlVisma(externalAuthState.id).then((response) => {
      if (response) {
        window.location.replace(response);
      }
    });
  };

  const redirectToBank = async (bankName, bankCountry, psuType) => {
    await BankService.getAuthLinkForEnableBanking(
      externalAuthState.targetClient,
      bankName,
      bankCountry,
      psuType,
      externalAuthState.id
    )
      .then((response) => {
        if (response) {
          window.location.replace(response.data);
        }
      })
      .catch(() => {
        setAuthState(AUTHSTATE.ERROR);
        setAuthErrorType(AUTHSTATE.BANK);
        setAuthErrorText(gT(t, '.error.redirect-to-bank'));
      });
  };

  const openSkvTab = async () => {
    if (skvUrl.length !== 0) {
      window.open(skvUrl, '_blank', 'noopener,noreferrer');
      await AuthService.createOnboardingEvent(externalAuthState.id, 3, null);
    } else {
      setAuthErrorType(AUTHSTATE.SKV);
      setAuthErrorText(gT(t, '.error.redirect-to-skv'));
    }
  };

  const nextStep = async () => {
    if (authState === AUTHSTATE.BANK) {
      setAuthState(AUTHSTATE.ACCOUNTING);
    } else if (authState === AUTHSTATE.ACCOUNTING) {
      setAuthState(AUTHSTATE.SKV);
    }
  };

  const previousStep = async () => {
    if (authState === AUTHSTATE.SKV) {
      setAuthState(AUTHSTATE.ACCOUNTING);
    } else if (authState === AUTHSTATE.ACCOUNTING) {
      if (banks.length === 0 && externalAuthState.bankConnected === false) {
        await getBanks(selectedCountry.code)
          .then(() => setAuthState(AUTHSTATE.BANK))
          .catch(() => {});
      } else {
        setAuthState(AUTHSTATE.BANK);
      }
    }
  };

  const getAuthStep = () => {
    switch (authState) {
      case AUTHSTATE.ACCOUNTING:
        return 1;
      case AUTHSTATE.BANK:
        return 3;
      case AUTHSTATE.SKV:
        return 2;
      default:
        return 0;
    }
  };

  return (
    <div className="external-auth-wrapper">
      <XpektorModal>
        {logo && (
          <div className="auth-header">
            <img className="logo" src={logo} alt="Logo" />
          </div>
        )}
        <XpektorModal.Body>
          {authState === AUTHSTATE.LOADING ? (
            <LoadingBar active status={gT(t, '.loading-bar')} />
          ) : (
            externalAuthState !== null && (
              <>
                <AuthStepDisplay step={getAuthStep()}>
                  <AuthStepIcon
                    selected={authState === AUTHSTATE.BANK}
                    connected={externalAuthState.bankConnected === true}
                    text={gT(t, '.auth-step-icon-text.bank')}
                  />
                  <AuthStepIconLine
                    active={externalAuthState.bankConnected === true}
                  />
                  <AuthStepIcon
                    selected={authState === AUTHSTATE.ACCOUNTING}
                    connected={externalAuthState.accountingConnected === true}
                    text={gT(t, '.auth-step-icon-text.accounting')}
                  />
                  <AuthStepIconLine
                    active={externalAuthState.accountingConnected === true}
                  />
                  <AuthStepIcon
                    selected={authState === AUTHSTATE.SKV}
                    connected={externalAuthState.skvConnected === true}
                    text={gT(t, '.auth-step-icon-text.skv')}
                  />
                </AuthStepDisplay>
                {authState === AUTHSTATE.ACCOUNTING && (
                  <AccountingAuthStep
                    completed={externalAuthState.accountingConnected === true}
                    redirectToFortnox={redirectToFortnox}
                    redirectToVisma={redirectToVisma}
                    error={authErrorType === AUTHSTATE.ACCOUNTING}
                    errorText={authErrorText}
                    extStateId={externalAuthState.id}
                    onValidation={() => {
                      setExternalAuthState(() => ({
                        ...externalAuthState,
                        accountingConnected: true,
                      }));
                    }}
                    t={t}
                  />
                )}
                {authState === AUTHSTATE.BANK && (
                  <BankAuthStep
                    completed={externalAuthState.bankConnected === true}
                    banks={banks}
                    countries={countries}
                    selectedCountry={selectedCountry}
                    onCountryClick={(country) => {
                      if (country.name !== selectedCountry.name) {
                        setSelectedCountry(country);
                        setBanks([]);
                        getBanks(country.code);
                      }
                    }}
                    selectBank={(bank) => {
                      setSelectedBank(bank);
                      setShowBankTypeModal(true);
                    }}
                    error={authErrorType === AUTHSTATE.BANK}
                    errorText={authErrorText}
                    t={t}
                  />
                )}
                {authState === AUTHSTATE.SKV && (
                  <SkvAuthStep
                    allowed={
                      externalAuthState.accountingConnected === true ||
                      externalAuthState.bankConnected === true
                    }
                    completed={externalAuthState.skvConnected === true}
                    openSkvTab={openSkvTab}
                    error={authErrorType === AUTHSTATE.SKV}
                    errorText={authErrorText}
                    loading={fetchingSkvUrl}
                    t={t}
                  />
                )}
                {authState !== AUTHSTATE.ERROR &&
                  authState !== AUTHSTATE.COMPLETED && (
                    <div className="auth-step-nav">
                      {authState !== AUTHSTATE.BANK && (
                        <ArrowButton
                          content={gT(t, '.arrow-btn.previous')}
                          icon={faArrowLeft}
                          iconPosition="left"
                          onClick={() => previousStep()}
                        />
                      )}
                      {authState === AUTHSTATE.BANK && <div />}
                      {authState !== AUTHSTATE.SKV ? (
                        externalAuthState.targetClient !== null && (
                          <ArrowButton
                            content={
                              (authState === AUTHSTATE.ACCOUNTING &&
                                !externalAuthState.accountingConnected) ||
                              (authState === AUTHSTATE.BANK &&
                                !externalAuthState.bankConnected)
                                ? gT(t, '.arrow-btn.skip')
                                : gT(t, '.arrow-btn.next')
                            }
                            onClick={() => nextStep()}
                            icon={faArrowRight}
                            iconPosition="right"
                          />
                        )
                      ) : externalAuthState.accountingConnected === true ||
                        externalAuthState.bankConnected === true ? (
                        <XpektorButton
                          icon={faCheck}
                          content={gT(t, '.xpektor-btn.complete')}
                          onClick={() => setAuthState(AUTHSTATE.COMPLETED)}
                        />
                      ) : (
                        ''
                      )}
                    </div>
                  )}
              </>
            )
          )}
          {authState === AUTHSTATE.ERROR && (
            <div className="auth-step">
              <div className="auth-default-error">
                <p>{errorText}</p>
              </div>
            </div>
          )}
          {authState === AUTHSTATE.COMPLETED && (
            <div className="auth-step">
              <div className="auth-step-completed-content">
                <FontAwesomeIcon
                  className="auth-complete-icon"
                  icon={faCircleCheck}
                />
                <p className="auth-complete-header">
                  {externalAuthState.accountingConnected &&
                  externalAuthState.bankConnected &&
                  externalAuthState.skvConnected
                    ? gT(t, '.auth-complete-header.accounting-bank-skv')
                    : !externalAuthState.accountingConnected
                    ? gT(t, '.auth-complete-header.bank')
                    : !externalAuthState.bankConnected
                    ? gT(t, '.auth-complete-header.accounting')
                    : gT(t, '.auth-complete-header.accounting-bank')}
                </p>
                <p className="auth-complete-text">
                  {externalAuthState.accountingConnected &&
                  externalAuthState.bankConnected &&
                  externalAuthState.skvConnected
                    ? gT(t, '.auth-complete-text.accounting-bank-skv')
                    : !externalAuthState.accountingConnected
                    ? gT(t, '.auth-complete-text.bank')
                    : !externalAuthState.bankConnected
                    ? gT(t, '.auth-complete-text.accounting')
                    : gT(t, '.auth-complete-text.accounting-bank')}
                </p>
              </div>
            </div>
          )}
        </XpektorModal.Body>
        <div className="auth-footer">
          <p className="why-link">
            {gT(t, '.why-link.why-info-important')}&nbsp;
            <a href="/why" target="_blank" rel="noopener noreferrer">
              {gT(t, '.why-link.read-more')}
            </a>
          </p>
          <div className="powered-by">
            <p>{gT(t, '.powered-by')}</p>
            <img className="logo" src={XpektorLogo} alt="XpektorLogo" />
          </div>
        </div>
      </XpektorModal>
      {showBankTypeModal && (
        <BankTypeModal
          bank={selectedBank}
          redirectToBank={redirectToBank}
          handleClose={() => setShowBankTypeModal(false)}
          t={t}
        />
      )}
    </div>
  );
}
